import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import { Printer, ImageDown, Share2, Save, Sparkles, GraduationCap, Zap, Flame, Plus, CheckCircle2, Lock, Dumbbell } from "lucide-react";
import { ExerciseCard } from "@/components/ExerciseCard";
import { useAuth } from "@/context/AuthContext";
import { api, API, pollAiJob } from "@/lib/api";
import { tr, AI_LANG_NAME, muscleName } from "@/data/i18n";
import { EXERCISES, MUSCLE_LIST } from "@/data/exercises";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

export function WorkoutView({ plan, setPlan, editable = false }) {
  const { user, subscription, lang } = useAuth();
  const navigate = useNavigate();
  const [beginner, setBeginner] = useState(plan.level === "Principiante");
  const [busy, setBusy] = useState("");
  const [activeDay, setActiveDay] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [logDate, setLogDate] = useState(new Date().toISOString().slice(0, 10));
  const [logNote, setLogNote] = useState("");
  const [logExercises, setLogExercises] = useState([]);
  const [libOpen, setLibOpen] = useState(false);
  const [libDay, setLibDay] = useState(0);
  const [libFilter, setLibFilter] = useState("Tutti");
  const areaRef = useRef(null);

  const hasAccess = subscription?.has_access;
  const locked = !editable && !hasAccess;

  const requireLogin = () => {
    if (!user) { toast.error(tr(lang, "login_required")); return true; }
    return false;
  };
  const requireAccess = () => {
    if (!hasAccess) { toast.error(tr(lang, "need_sub")); navigate("/pricing"); return true; }
    return false;
  };

  const handlePrint = () => window.print();

  const handlePng = async () => {
    if (!areaRef.current) return;
    setBusy("png");
    setExporting(true);
    await new Promise((r) => setTimeout(r, 60));
    try {
      const canvas = await html2canvas(areaRef.current, { backgroundColor: "#09090B", scale: 2, useCORS: true });
      const link = document.createElement("a");
      link.download = `${(plan.title || "scheda").replace(/\s+/g, "_")}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (e) { toast.error("Errore export PNG"); }
    setExporting(false);
    setBusy("");
  };

  const addDay = () => {
    const days = [...(plan.days || [])];
    days.push({ name: `${tr(lang, "day")} ${days.length + 1}`, focus: "", exercises: [] });
    setPlan({ ...plan, days });
    setActiveDay(days.length - 1);
  };

  const removeDay = (di) => {
    const days = plan.days.filter((_, k) => k !== di);
    setPlan({ ...plan, days });
    setActiveDay(0);
  };

  const addExerciseToDay = (di, ex) => {
    const days = [...plan.days];
    days[di] = { ...days[di], exercises: [...(days[di].exercises || []), ex] };
    setPlan({ ...plan, days });
  };

  const addBlankExercise = (di) => addExerciseToDay(di, {
    name: "", primary_muscle: "Petto", equipment: "", sets: "3", reps: "10", rest: "90s",
    load: "", notes: "", execution: [], beginner_tip: "", common_mistakes: "", video_query: "",
  });

  const openLibrary = (di) => { setLibDay(di); setLibFilter("Tutti"); setLibOpen(true); };

  const addFromLibrary = (ex) => {
    addExerciseToDay(libDay, { ...ex, muscle: ex.primary_muscle });
    setLibOpen(false);
  };

  const doLog = async () => {
    setBusy("log");
    try {
      const day = plan.days?.[activeDay];
      await api.createLog({
        plan_id: plan.id, plan_title: plan.title || "Scheda",
        day_name: day?.name || `${tr(lang, "day")} ${activeDay + 1}`,
        date: logDate, notes: logNote,
        exercises: logExercises.filter((e) => e.weight || e.reps),
      });
      toast.success(tr(lang, "log_saved"));
      setLogOpen(false);
      setLogNote("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    setBusy("");
  };

  const openLog = () => {
    if (requireLogin()) return;
    const day = plan.days?.[activeDay];
    setLogExercises((day?.exercises || []).map((ex) => ({ name: ex.name, weight: "", reps: "" })));
    setLogOpen(true);
  };

  const persist = async () => {
    const { data } = await api.savePlan(plan);
    setPlan && setPlan(data);
    return data;
  };

  const handleSave = async () => {
    if (requireLogin() || requireAccess()) return;
    setBusy("save");
    try { await persist(); toast.success(tr(lang, "plan_saved")); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    setBusy("");
  };

  const handleShare = async () => {
    if (requireLogin() || requireAccess()) return;
    setBusy("share");
    try {
      const saved = await persist();
      const url = `${window.location.origin}/plan/${saved.id}`;
      await navigator.clipboard.writeText(url);
      toast.success(tr(lang, "link_copied"));
    } catch (e) { toast.error("Errore condivisione"); }
    setBusy("");
  };

  const handleEvolve = async () => {
    if (requireLogin() || requireAccess()) return;
    setBusy("evolve");
    try {
      const { data } = await api.evolve(plan, AI_LANG_NAME[lang]);
      const result = await pollAiJob(data.job_id);
      setPlan && setPlan(result);
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("Scheda evoluta! 💪");
    } catch (e) { toast.error(e?.response?.data?.detail || e?.message || "Errore evoluzione"); }
    setBusy("");
  };

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-8">
      {/* Toolbar */}
      <div className="no-print mb-6 flex flex-wrap items-center gap-2">
        <ToolBtn onClick={handlePrint} icon={<Printer size={16} />} label={tr(lang, "export_print")} testid="export-print-btn" />
        <ToolBtn onClick={handlePng} icon={<ImageDown size={16} />} label={tr(lang, "export_png")} loading={busy === "png"} testid="export-png-btn" />
        <ToolBtn onClick={handleShare} icon={<Share2 size={16} />} label={tr(lang, "export_share")} loading={busy === "share"} testid="export-share-btn" />
        <ToolBtn onClick={handleSave} icon={<Save size={16} />} label={tr(lang, "save_plan")} loading={busy === "save"} testid="save-plan-btn" primary />
        <ToolBtn onClick={openLog} icon={<CheckCircle2 size={16} />} label={tr(lang, "mark_done")} loading={busy === "log"} testid="mark-done-btn" />
        <div className="ml-auto flex items-center gap-2 rounded-full border border-zinc-800 bg-zinc-900 px-3 py-1.5">
          <GraduationCap size={16} className={beginner ? "text-red-500" : "text-zinc-500"} />
          <span className="text-sm text-zinc-300">{tr(lang, "beginner_mode")}</span>
          <button onClick={() => setBeginner(!beginner)}
                  className={`relative h-5 w-9 rounded-full transition-colors ${beginner ? "bg-red-500" : "bg-zinc-700"}`}
                  data-testid="beginner-mode-toggle" aria-label="beginner-mode">
            <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${beginner ? "translate-x-4" : "translate-x-0.5"}`} />
          </button>
        </div>
      </div>

      {/* Printable area */}
      <div ref={areaRef} className="print-area" data-testid="workout-detail-view">
        <div className="relative overflow-hidden rounded-3xl border border-zinc-800 bg-gradient-to-br from-zinc-900 to-zinc-950 p-6 sm:p-8">
          <div className="flex items-center gap-2 text-red-500">
            <Flame size={18} /><span className="font-display text-sm font-bold uppercase tracking-widest">VOLT</span>
            {plan.generation > 1 && (
              <span className="ml-2 rounded-full bg-red-500/20 px-2.5 py-0.5 text-xs font-bold text-red-400">
                {tr(lang, "generation")} {plan.generation}
              </span>
            )}
          </div>
          {editable ? (
            <input value={plan.title} onChange={(e) => setPlan({ ...plan, title: e.target.value })}
                   placeholder={tr(lang, "title_ph")}
                   className="mt-2 w-full bg-transparent font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-white outline-none placeholder:text-zinc-700"
                   data-testid="workout-title-input" />
          ) : (
            <h1 className="mt-2 font-display text-3xl sm:text-5xl font-extrabold uppercase tracking-tight text-white">{plan.title}</h1>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {[plan.goal, plan.split_type, plan.equipment, plan.level].filter(Boolean).map((b, i) => (
              <span key={i} className="rounded-full border border-zinc-700 bg-zinc-800/60 px-3 py-1 text-xs font-medium text-zinc-300">{b}</span>
            ))}
          </div>
          {plan.overview && (
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-zinc-400">{plan.overview}</p>
          )}
        </div>

        {/* Day tabs */}
        {(plan.days?.length > 1 || editable) && (
          <div className="no-print mt-6 flex flex-wrap items-center gap-2" data-testid="day-tabs">
            {plan.days?.map((day, i) => (
              <button key={i} onClick={() => setActiveDay(i)} data-testid={`day-tab-${i}`}
                      className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold uppercase tracking-wide transition-all ${
                        activeDay === i ? "bg-red-500 text-white red-glow" : "border border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-red-500/40"}`}>
                {tr(lang, "day")} {i + 1}
                {editable && plan.days.length > 1 && (
                  <span onClick={(e) => { e.stopPropagation(); removeDay(i); }} className="text-white/70 hover:text-white">×</span>
                )}
              </button>
            ))}
            {editable && (
              <button onClick={addDay} data-testid="add-day-btn"
                      className="inline-flex items-center gap-1 rounded-full border border-dashed border-zinc-700 px-4 py-2 text-sm font-semibold text-zinc-400 hover:border-red-500/40">
                <Plus size={15} /> {tr(lang, "day")}
              </button>
            )}
          </div>
        )}

        <div className="mt-6 space-y-8">
          {plan.days?.map((day, di) => {
            const hidden = plan.days.length > 1 && activeDay !== di && !exporting;
            const dayLocked = locked && di > 0;
            return (
            <div key={di} className={`day-block ${hidden ? "hidden" : ""}`}>
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-9 items-center rounded-lg bg-red-500 px-3.5 font-display text-base font-bold uppercase text-white">
                  {day.name && /giorno|day|día|jour|tag/i.test(day.name) ? day.name : `${tr(lang, "day")} ${di + 1}${day.name ? " · " + day.name : ""}`}
                </div>
                {day.focus && <span className="text-sm text-zinc-500">{day.focus}</span>}
              </div>
              {dayLocked ? (
                <div className="relative overflow-hidden rounded-2xl border border-red-500/30" data-testid="paywall-locked-day">
                  <div className="pointer-events-none select-none space-y-3 p-4 blur-md" aria-hidden>
                    {(day.exercises || []).slice(0, 3).map((ex, k) => (
                      <div key={k} className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-5">
                        <div className="h-5 w-1/2 rounded bg-zinc-700" />
                        <div className="mt-3 grid grid-cols-4 gap-2">{[0,1,2,3].map((z) => <div key={z} className="h-8 rounded bg-zinc-800" />)}</div>
                      </div>
                    ))}
                  </div>
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-zinc-950/60 p-6 text-center backdrop-blur-sm">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-red-500 red-glow"><Lock className="text-white" /></div>
                    <h4 className="font-display text-2xl font-extrabold uppercase text-white">{tr(lang, "unlock_title")}</h4>
                    <p className="max-w-md text-sm text-zinc-300">{tr(lang, "unlock_sub")}</p>
                    <button onClick={() => navigate("/pricing")} data-testid="paywall-unlock-btn"
                            className="mt-1 inline-flex items-center gap-2 rounded-full bg-red-500 px-6 py-3 font-bold text-white hover:bg-red-600 red-glow">
                      <Zap size={16} /> {tr(lang, "unlock_cta")}
                    </button>
                  </div>
                </div>
              ) : (
              <div className="space-y-3">
                {day.exercises?.map((ex, ei) => (
                  <ExerciseCard key={ei} exercise={ex} index={ei} beginner={beginner} lang={lang} editable={editable}
                    onChange={(nex) => {
                      const days = [...plan.days];
                      days[di] = { ...day, exercises: day.exercises.map((e, k) => (k === ei ? nex : e)) };
                      setPlan({ ...plan, days });
                    }}
                    onRemove={() => {
                      const days = [...plan.days];
                      days[di] = { ...day, exercises: day.exercises.filter((_, k) => k !== ei) };
                      setPlan({ ...plan, days });
                    }} />
                ))}
                {day.exercises?.length === 0 && !editable && (
                  <p className="rounded-xl border border-dashed border-zinc-800 p-6 text-center text-sm text-zinc-600">
                    {tr(lang, "add_exercise")}...
                  </p>
                )}
                {editable && (
                  <div className="no-print flex flex-wrap gap-2 pt-1">
                    <button onClick={() => addBlankExercise(di)} data-testid={`add-blank-exercise-${di}`}
                            className="inline-flex items-center gap-1.5 rounded-full bg-red-500 px-4 py-2 text-sm font-semibold text-white hover:bg-red-600 red-glow">
                      <Plus size={15} /> {tr(lang, "write_freely")}
                    </button>
                    <button onClick={() => openLibrary(di)} data-testid={`add-library-exercise-${di}`}
                            className="inline-flex items-center gap-1.5 rounded-full border border-zinc-700 bg-zinc-900 px-4 py-2 text-sm font-semibold text-zinc-300 hover:border-red-500/40">
                      <Dumbbell size={15} /> {tr(lang, "add_from_library")}
                    </button>
                  </div>
                )}
              </div>
              )}
            </div>
          );})}
        </div>

        {/* Branded footer (shown in export) */}
        <div className="mt-6 flex items-center justify-center gap-2 text-xs text-zinc-600">
          <Flame size={13} className="text-red-500" />
          <span className="font-display font-bold uppercase tracking-widest text-zinc-500">{tr(lang, "made_with")}</span>
          <span>· {typeof window !== "undefined" ? window.location.host : "voltapp"}</span>
        </div>
      </div>

      {/* Global unlock banner */}
      {locked && (
        <div className="no-print mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-red-500/40 bg-gradient-to-r from-red-500/15 to-orange-500/10 p-5" data-testid="paywall-banner">
          <div className="flex items-center gap-3">
            <Lock className="text-red-400" size={20} />
            <div>
              <div className="font-heading font-bold text-white">{tr(lang, "unlock_title")}</div>
              <div className="text-sm text-zinc-400">{tr(lang, "unlock_sub")}</div>
            </div>
          </div>
          <button onClick={() => navigate("/pricing")} className="rounded-full bg-red-500 px-6 py-2.5 font-bold text-white hover:bg-red-600 red-glow">{tr(lang, "unlock_cta")}</button>
        </div>
      )}

      {/* Progression engine */}
      {plan.source === "ai" && (
        <div className="no-print mt-8 rounded-2xl border border-red-500/30 bg-gradient-to-r from-red-500/10 to-orange-500/10 p-6 text-center">
          <Sparkles className="mx-auto mb-2 text-red-500" />
          <p className="mb-3 text-sm text-zinc-300">Evoluzione progressiva: genera la scheda successiva con sovraccarico.</p>
          <button onClick={handleEvolve} disabled={busy === "evolve"}
                  className="inline-flex items-center gap-2 rounded-full bg-red-500 px-6 py-2.5 font-semibold text-white transition-all hover:bg-red-600 disabled:opacity-60"
                  data-testid="progression-evolve-btn">
            <Zap size={16} /> {busy === "evolve" ? tr(lang, "evolving") : tr(lang, "evolve")}
          </button>
        </div>
      )}

      {/* Log workout dialog */}
      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto border-zinc-800 bg-zinc-950 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-white">{tr(lang, "mark_done")}</DialogTitle>
            <DialogDescription className="text-zinc-500">{plan.days?.[activeDay]?.name || `${tr(lang, "day")} ${activeDay + 1}`}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-zinc-400">{tr(lang, "date_lbl")}</label>
              <input type="date" value={logDate} onChange={(e) => setLogDate(e.target.value)} data-testid="log-date-input"
                     className="w-full rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-sm text-white outline-none focus:border-red-500" />
            </div>
            {logExercises.length > 0 && (
              <div className="space-y-2">
                <div className="flex gap-2 px-1 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                  <span className="flex-1">Esercizio</span><span className="w-20 text-center">{tr(lang, "weight_lbl")}</span><span className="w-16 text-center">{tr(lang, "reps_lbl")}</span>
                </div>
                {logExercises.map((le, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="flex-1 truncate text-sm text-zinc-300">{le.name}</span>
                    <input type="number" value={le.weight} onChange={(e) => setLogExercises((p) => p.map((x, k) => k === i ? { ...x, weight: e.target.value } : x))}
                           data-testid={`log-weight-${i}`} placeholder="kg"
                           className="w-20 rounded-lg border border-zinc-700 bg-zinc-900 px-2 py-2 text-center text-sm text-white outline-none focus:border-red-500" />
                    <input type="number" value={le.reps} onChange={(e) => setLogExercises((p) => p.map((x, k) => k === i ? { ...x, reps: e.target.value } : x))}
                           data-testid={`log-reps-${i}`} placeholder="rip"
                           className="w-16 rounded-lg border border-zinc-700 bg-zinc-900 px-2 py-2 text-center text-sm text-white outline-none focus:border-red-500" />
                  </div>
                ))}
              </div>
            )}
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-zinc-400">{tr(lang, "note_lbl")}</label>
              <textarea value={logNote} onChange={(e) => setLogNote(e.target.value)} rows={2} placeholder={tr(lang, "log_note_ph")} data-testid="log-note-input"
                        className="w-full rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-sm text-white outline-none focus:border-red-500" />
            </div>
            <button onClick={doLog} disabled={busy === "log"} data-testid="log-confirm-btn"
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-red-500 py-3 font-bold text-white hover:bg-red-600 disabled:opacity-60">
              <CheckCircle2 size={18} /> {tr(lang, "mark_done")}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Exercise library dialog */}
      <Dialog open={libOpen} onOpenChange={setLibOpen}>
        <DialogContent className="max-h-[85vh] overflow-hidden border-zinc-800 bg-zinc-950 sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-white">{tr(lang, "add_from_library")}</DialogTitle>
            <DialogDescription className="text-zinc-500">{tr(lang, "day")} {libDay + 1}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-1.5 border-b border-zinc-800 pb-3">
            {["Tutti", ...MUSCLE_LIST].map((m) => (
              <button key={m} onClick={() => setLibFilter(m)}
                      className={`rounded-full px-3 py-1 text-xs font-medium ${libFilter === m ? "bg-red-500 text-white" : "bg-zinc-800 text-zinc-400"}`}>
                {m === "Tutti" ? "Tutti" : muscleName(m, lang)}
              </button>
            ))}
          </div>
          <div className="max-h-[55vh] space-y-2 overflow-y-auto pr-1">
            {EXERCISES.filter((ex) => libFilter === "Tutti" || ex.primary_muscle === libFilter).map((ex, i) => (
              <button key={i} onClick={() => addFromLibrary(ex)} data-testid={`library-exercise-${i}`}
                      className="flex w-full items-center justify-between rounded-xl border border-zinc-800 bg-zinc-900 p-3 text-left transition-all hover:border-red-500/40">
                <div>
                  <div className="font-heading font-semibold text-white">{ex.name}</div>
                  <div className="text-xs text-zinc-500">{muscleName(ex.primary_muscle, lang)} · {ex.equipment}</div>
                </div>
                <Plus size={18} className="text-red-400" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ToolBtn({ onClick, icon, label, loading, primary, testid }) {
  return (
    <button onClick={onClick} disabled={loading} data-testid={testid}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-all disabled:opacity-60 ${
              primary ? "bg-red-500 text-white hover:bg-red-600 red-glow" : "border border-zinc-800 bg-zinc-900 text-zinc-200 hover:border-red-500/40"}`}>
      {icon} {label}
    </button>
  );
}
